import { TeApiContext } from '@ecommerce/nest-ssr/common';
import Box from 'atomic/box';
import Container from 'atomic/container';
import Icon from 'atomic/icon';
import classNames from 'classnames';
import scrollTop from 'helpers/scroll-page';
import { ProducerBadgeConfig } from 'interfaces/producer.interface';
import scrollToElement, { ELEMENT_SCROLL_TOP_SPACE } from 'modules/product/helpers/scroll-to-element';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import './product-tab.scss';

interface TabListItemInterface {
    index: number;
    active: boolean;
    label: string;
    count?: number | undefined;
    href: string;
}

interface ProductTabProps {
    opinionsCount: number;
    offersCount: number;
    producerBadges?: ProducerBadgeConfig;
    onTabClick?: (tab: string) => void;
    isQuestions?: boolean;
}

const ProductTab: React.FC<ProductTabProps> = ({
    opinionsCount,
    offersCount,
    producerBadges,
    onTabClick,
    isQuestions = false
}) => {
    const api = useContext(TeApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const TabListInitStateDesktop: TabListItemInterface[] = [
        {
            index: 0,
            active: true,
            label: 'Oferty',
            count: offersCount,
            href: '#ceny'
        },
        {
            index: 1,
            active: false,
            label: 'Opis i specyfikacja',
            href: '#opis'
        },
        {
            index: 2,
            active: false,
            label: 'Opinie o produkcie',
            count: opinionsCount,
            href: '#opinie'
        },
        {
            index: 3,
            active: false,
            label: 'Sprawdź również',
            href: '#polecane'
        }
    ];

    if (isQuestions) {
        TabListInitStateDesktop.push({
            index: 4,
            active: false,
            label: 'Pytania i odpowiedzi',
            href: '#pytania'
        });
    }

    const TabListInitStateMobile: TabListItemInterface[] = [
        {
            index: 0,
            active: true,
            label: 'Oferty',
            href: '#ceny'
        },
        {
            index: 1,
            active: false,
            label: 'O produkcie',
            href: '#product-details'
        }
    ];

    const [tabList, setTabList] = useState(api.getDevice() === 'desktop' ? TabListInitStateDesktop : TabListInitStateMobile);
    const tabsRef = useRef<any>([]);

    const [tabUnderlineWidth, setTabUnderlineWidth] = useState(api.getDevice() === 'desktop' ? 99 : 72);
    const [tabUnderlineLeft, setTabUnderlineLeft] = useState(0);

    const setActiveTab = useCallback((item: TabListItemInterface) => {
        setTabList(prevState => {
            return prevState.map(obj => {
                if (obj.index === item.index) {
                    const currentTab = tabsRef.current[obj.index];

                    setTabUnderlineLeft(currentTab?.offsetLeft ?? 0);
                    setTabUnderlineWidth(currentTab?.clientWidth ?? 0);

                    return { ...obj, active: true };
                } else {
                    return { ...obj, active: false };
                }
            });
        });
    }, [setTabList]);

    const scrollToTab = useCallback(() => {
        const hash = window.location.hash;
        const item = tabList.find(item => item.href === hash);

        if (item) {
            scrollToElement(item.href.substring(1));
        } else if (hash === '#attributes') {
            scrollToElement('attributes');
        }
    }, [tabList]);

    useEffect(() => {
        window.addEventListener('hashchange', scrollToTab, { passive: true });

        return (): void => window.removeEventListener('hashchange', scrollToTab);
    }, [scrollToTab]);

    useEffect(() => {
        const sections: HTMLElement[] = Array.from(document.getElementsByClassName('tab-section')) as HTMLElement[];

        const handleScroll = (): void => {
            let currentHash = '';

            sections.forEach((section) => {
                const { top } = section.getBoundingClientRect();

                if (top <= ELEMENT_SCROLL_TOP_SPACE) {
                    currentHash = `#${section.getAttribute('id')}`;
                }
            });

            const item = tabList.find(item => item.href === currentHash);

            if (item) {
                setActiveTab(item);
            } else if (currentHash === '#attributes') {
                setActiveTab(tabList[1]);
            }
        };

        window.addEventListener('scroll', handleScroll);

        return (): void => window.removeEventListener('scroll', handleScroll);
    }, [tabList, setActiveTab]);

    useEffect(() => {
        TabListInitStateDesktop[0].count = offersCount;

        setTabList(api.getDevice() === 'desktop' ? TabListInitStateDesktop : TabListInitStateMobile);
    }, [offersCount]);

    const onTab = (tab: string): void => {
        if (onTabClick) {
            onTabClick(tab);
        } else {
            scrollToTab();
        }
    };

    return (
        <Box className="product-tab">
            <Container className="product-tab__container">
                <div className="product-tab__list">
                    {tabList.map((tab, idx) => (
                        <a
                            ref={(el) => (tabsRef.current[idx] = el)}
                            href={tab.href}
                            className={classNames({
                                'product-tab__list__item': true,
                                'product-tab__list__item--active': tab.active,
                                'mobile': api.getDevice() !== 'desktop'
                            })}
                            key={tab.href}
                            onClick={(): void => onTab(tab.href)}
                        >
                            {tab.label}  {tab.count ? `(${tab.count})` : void 0}
                        </a>
                    ))}
                    <span
                        className="product-tab__list__item-bar"
                        style={{ left: tabUnderlineLeft, width: tabUnderlineWidth }}
                    />
                </div>
                {(producerBadges) && (
                    <div
                        className={classNames({
                            'product-tab__producer-logo': true,
                            'product-tab__producer-logo--mobile': api.getDevice() !== 'desktop'
                        })}
                    >
                        {React.createElement(producerBadges.url ? 'a' : 'div', {
                            href: producerBadges.url ? producerBadges.url : void 0
                        }, (
                            <img
                                src={producerBadges.image}
                                height={50}
                                width={60}
                                alt={producerBadges.title}
                                aria-label="Logo producenta"
                            />
                        ))}
                    </div>
                )}
                {api.getDevice() === 'desktop' && (
                    <div className="product-tab__scroll-top">
                        <Icon
                            name="top"
                            color="secondary"
                            size={26}
                            onClick={scrollTop}
                        />
                    </div>
                )}
            </Container>
        </Box>
    );
};

export default ProductTab;
